import axios from 'axios';
import { usePage } from '@inertiajs/vue3';
import { eventBus } from '@/Mixins/eventbus.js';
import i18n from '@/i18n/i18n.js';

const inertiaProps = usePage();
const t = i18n.global.t;

function getCookieInBoot(cname) {
    const name = cname + '=';
    const decodedCookie = decodeURIComponent (document.cookie);
    const ca = decodedCookie.split (';');
    for(let i = 0; i <ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === ' ') {
            c = c.substring(1);
        }
        if (c.indexOf(name) === 0) {
            return c.substring(name.length, c.length);
        }
    }
    return "";
}

const axiosInstance = axios.create({
    headers: {
        'X-Requested-With': 'XMLHttpRequest',
    },
    withCredentials: true, // Always send cookies with requests
});

axiosInstance.interceptors.request.use(
    (config) => {
        const xsrfToken = getCookieInBoot('XSRF-TOKEN');
        if (xsrfToken) {
            config.headers['X-XSRF-TOKEN'] = xsrfToken;
        }

        const csrfToken = inertiaProps?.props?.csrf_token;
        if (csrfToken) {
            config.headers['X-CSRF-TOKEN'] = csrfToken;
        }

        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

axiosInstance.interceptors.response.use(
    (response) => {
        return response;
    },
    (error) => {
        if (error?.config?.suppressError) {         // If suppressError is set, we skip processing
            return Promise.reject(error);
        }

        if (!error?.response || error?.code === 'ERR_NETWORK') {
            if (error?.code === 'ERR_CANCELED') {
                return Promise.reject(error);
            }
            // SM-4824 - [Alert] Hide Error Alerts
            // eventBus.emit('AlertModal', {
            //     title: t('errors.networkError'),
            //     text: t('errors.networkErrorDescription'),
            //     showIcon: 'red',
            //     button2: t('shared.ok'),
            // });

            console.error('Network error: ', error);
            return Promise.reject(error);
        }

        let text =
            error?.response?.data?.message
            || error?.response?.data?.error
            || error?.response?.data?.errors?.[Object.keys(error?.response?.data?.errors || {})[0]]?.[0]
            || '';

        // Check if this is an S3/CDN file access error (403 or 404) - suppress popup for these
        const requestUrl = error?.config?.url || error?.request?.responseURL || '';
        const fullUrl = requestUrl.startsWith('http') ? requestUrl : (error?.config?.baseURL || '') + requestUrl;
        const isFileAccessError = error?.response?.status === 403 || error?.response?.status === 404;

        // Check if this is a feed API request (401 during initial load is expected if not authenticated)
        const isFeedRequest = requestUrl && (
            requestUrl.includes('/api/feed/') ||
            requestUrl.includes('/api/content/recent/') ||
            requestUrl.includes('/api/coaching')
        );
        const isUnauthorizedFeedError = error?.response?.status === 401 && isFeedRequest;

        // Get error message text for checking (before it gets translated)
        const errorMessage = text || error?.response?.data?.message || '';
        const errorMessageLower = errorMessage.toLowerCase();

        // Detect S3 file errors - these should be suppressed (no popup, minimal logging)
        const requestMethod = error?.config?.method?.toUpperCase() || 'GET';
        const isS3FileError = isFileAccessError && (
            // If suppressError flag is set, don't show popup
            error?.config?.suppressError === true ||
            // Check if request URL is an S3/CDN URL (check both relative and absolute)
            (fullUrl && (
                fullUrl.includes('.s3.') ||
                fullUrl.includes('s3.amazonaws.com') ||
                fullUrl.includes('cloudfront.net') ||
                fullUrl.includes('/cdn/') ||
                fullUrl.includes('cdn.') ||
                fullUrl.includes('ms-studio-storage')
            )) ||
            // Check if it's a file download/access endpoint or media file
            (requestUrl && (
                requestUrl.includes('/s3-url') ||
                requestUrl.includes('/download') ||
                requestUrl.includes('/screenshare') ||
                requestUrl.includes('/participant') ||
                requestUrl.includes('/recording/') ||
                requestUrl.includes('/media/') ||
                requestUrl.includes('/screenshares') ||
                requestUrl.includes('/subtitles/') ||
                requestUrl.includes('/thumbnails/') ||
                requestUrl.includes('.vtt') ||
                requestUrl.includes('.srt') ||
                requestUrl.includes('.webp') ||
                requestUrl.includes('.jpg') ||
                requestUrl.includes('.png')
            )) ||
            // Check if it's a HEAD request (file existence check) to S3/CDN
            (requestMethod === 'HEAD' && (
                fullUrl.includes('.s3.') ||
                fullUrl.includes('s3.amazonaws.com') ||
                fullUrl.includes('ms-studio-storage') ||
                requestUrl.includes('/media/')
            )) ||
            // Check response headers/content type for file responses
            (error?.response?.headers && (
                error.response.headers['content-type']?.includes('video/') ||
                error.response.headers['content-type']?.includes('audio/') ||
                error.response.headers['content-type']?.includes('image/') ||
                error.response.headers['content-type']?.includes('application/octet-stream') ||
                error.response.headers['content-type']?.includes('text/vtt') ||
                error.response.headers['content-type']?.includes('text/plain')
            )) ||
            // Check error message for file-related messages
            (errorMessageLower && (
                errorMessageLower.includes('file not found') ||
                errorMessageLower.includes('recording not found') ||
                errorMessageLower.includes('screenshare') ||
                errorMessageLower.includes('not found in storage') ||
                errorMessageLower.includes('not found on the server') ||
                errorMessageLower.includes('resource was not found') ||
                errorMessageLower.includes('forbidden')
            ))
        );

        switch (error?.response?.status) {
            case 400:
                text = t('errors.badRequest') + ': ' + text;
                break;
            case 401:
                // Suppress popup for feed requests (401 is expected when not authenticated yet)
                if (isUnauthorizedFeedError) {
                    console.debug('Feed request unauthorized (401) - user may not be authenticated yet:', requestUrl);
                    return Promise.reject(error);
                }
                text = t('errors.unauthorized');
                setTimeout(() => {
                    // window.location = '/';          // Logic, redirect to login page
                }, 2000);
                break;
            case 403:
                // Suppress popup for S3 file access errors - these are expected for private files
                // Don't log to console either - completely silent
                if (isS3FileError) {
                    // Return rejected promise for component handling, but don't show popup or log
                    return Promise.reject(error);
                }
                text = t('errors.forbidden');
                break;
            case 404:
                // Suppress popup for S3 file access errors (missing files) - these are expected
                // Don't log to console either - completely silent
                if (isS3FileError) {
                    // Return rejected promise for component handling, but don't show popup or log
                    return Promise.reject(error);
                }
                text = t('errors.notFound');
                break;
            case 413:
                text = t('errors.fileIsTooLarge');
                break;
            case 500:
                text = t('errors.serverError');
                break;
            case 503:
                text = t('errors.serviceUnavailable');
                break;
            default:
                text = text || t('errors.oopsSomethingWentWrong');
        }

        // If status is not 422, show alert modal
        // 422 is a validation error, so we don't want to show an alert modal
        // Also skip showing modal for S3 file errors (already handled above)
        // SM-4824 - [Alert] Hide Error Alerts
        if (error?.response?.status !== 422 && error?.response?.status !== 500 && !isS3FileError) {
            eventBus.emit('AlertModal', {
                title: t('shared.error'),
                text: text,
                showIcon: 'red',
                button2: t('shared.ok'),
            });
        }

        // Don't log errors for S3 file access errors, unauthorized feed errors, or when suppressError is set
        // S3 file errors (403/404) are completely silent - no console output
        if (!isS3FileError && !isUnauthorizedFeedError && !error?.config?.suppressError) {
            console.error('Axios error: ', error);
        } else if (isUnauthorizedFeedError) {
            // Suppress logging for unauthorized feed errors (expected when not authenticated)
            // Already logged above as debug
        }
        // S3 file errors are completely silent - no logging at all

        return Promise.reject(error);
    }
);

export default axiosInstance;
